import React from 'react';
import ImageProviderSelector from '../selectors/ImageProviderSelector';
import AgentToolsPanel from '../settings/AgentToolsPanel';
import AgentModelField from './AgentModelField';
import CarouselSizeField from './CarouselSizeField';
import VideoModelsField, { EditVideo } from './VideoModelsField';
import {
  AgentModelChoice,
  GENERATION_MAX_IMAGE_GENERATIONS,
  GENERATION_MODEL_CAP_USD,
  GenerationModel,
  GenerationSettings,
  ImageSize,
  SettingsAPI,
  buildGenerationPayload,
  parseGenerationSettings,
} from '../../api/settings';
import { MediaOutputSpec } from '../../api/structs/media-spec';
import { ImageProvider } from '../../api/structs/providers';
import { useToast } from '../../hooks/useToast';

const LOCKED_NOTE =
  'Pipelines marked to use these settings (the crew) always run on them; other pipelines are unchanged.';

/** The text of a number the user is typing; anything unparseable is not a number. */
const draftNumber = (draft: string): number => (draft.trim() === '' ? NaN : Number(draft));

const Heading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h4 className="text-[10px] uppercase tracking-widest text-[var(--app-text-muted)] mb-2">{children}</h4>
);

/**
 * The account's locked generation settings: the image model, the size of a
 * carousel's pictures, the set of video models, the model the agents work on,
 * what one agent step may spend on its own model calls, and how many images an
 * agent may generate. A pipeline marked to use them (the crew) runs on exactly
 * these and its agents cannot change them — only this form does.
 *
 * One Save, not a switch per field: a model and its size settings are a pair,
 * and half a pair stored is a run on the wrong model. The server checks the
 * whole record (the registry knows which models exist), so its refusal is shown
 * as it words it and the edits stay.
 */
const GenerationSettingsSection: React.FC = () => {
  const toast = useToast();
  const fieldId = React.useId();
  const [values, setValues] = React.useState<GenerationSettings>(() => parseGenerationSettings(undefined));
  // The two numbers are typed, so they live as text until Save: clamping while
  // typing would turn "0.6" into "0" on its way there.
  const [capDraft, setCapDraft] = React.useState(String(values.agent_model_cap_usd));
  const [generationsDraft, setGenerationsDraft] = React.useState(String(values.max_image_generations));
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState(false);
  // False while the image model lists sizes and not the carousel's; the field says so.
  const [carouselMade, setCarouselMade] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [error, setError] = React.useState('');

  const show = React.useCallback((record: GenerationSettings) => {
    setValues(record);
    setCapDraft(String(record.agent_model_cap_usd));
    setGenerationsDraft(String(record.max_image_generations));
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    void SettingsAPI.getGeneration()
      .then((record) => {
        if (cancelled) return;
        show(record);
        setLoadError(false);
      })
      // Never saved reads as the defaults (the server says so); a failed read is
      // not that, and the form must not pass the defaults off as the user's own.
      .catch(() => {
        if (!cancelled) setLoadError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [show]);

  // Any edit makes the last result stale.
  const edit = React.useCallback((change: (current: GenerationSettings) => GenerationSettings) => {
    setSaved(false);
    setError('');
    setValues(change);
  }, []);

  // Stable: the selectors list their callbacks as effect dependencies.
  const imageHandlers = React.useMemo(() => {
    const update = (change: (model: GenerationModel) => GenerationModel) =>
      edit((current) => ({ ...current, image: change(current.image) }));
    return {
      // A new provider has no model yet: the selector fills in that provider's
      // first one once its catalog answers, so a pair never mixes providers.
      // The provider already chosen keeps its model and sizes.
      onProviderChange: (provider: string) =>
        update((current) => (current.provider === provider ? current : { provider, model: '' })),
      onModelChange: (model: string) => update((current) => ({ ...current, model })),
      onSettingsChange: (settings: Partial<MediaOutputSpec>) =>
        update((current) => ({ provider: current.provider, model: current.model, settings })),
    };
  }, [edit]);

  const editVideo = React.useCallback<EditVideo>(
    (change) => edit((current) => ({ ...current, video: change(current.video) })),
    [edit]
  );
  const chooseCarouselSize = (carousel_image: ImageSize) => edit((current) => ({ ...current, carousel_image }));
  const chooseAgentModel = (agent_model: AgentModelChoice | null) => edit((current) => ({ ...current, agent_model }));

  const cap = draftNumber(capDraft);
  const generations = draftNumber(generationsDraft);
  // A model still empty is a provider whose catalog has not answered. The ranges
  // are the server's to enforce (it words the refusal); this only stops what
  // could not be sent at all.
  const complete =
    Number.isFinite(cap) &&
    Number.isFinite(generations) &&
    Boolean(values.image.model) &&
    carouselMade &&
    values.video.models.every((entry) => Boolean(entry.model));

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    setError('');
    const payload = buildGenerationPayload({
      ...values,
      agent_model_cap_usd: cap,
      max_image_generations: generations,
    });
    try {
      const record = await SettingsAPI.putGeneration(payload);
      // An answer that carries no system default (a bodiless 200) must not lose the one already known.
      show({ ...record, agent_model_default: record.agent_model_default ?? values.agent_model_default });
      setSaved(true);
      toast({ text: 'Generation settings saved', level: 'success' });
    } catch (err: any) {
      // The edits stay: the server names the field it refused.
      setError(err?.response?.data?.error || err?.message || 'Failed to save generation settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="mb-4 flex-shrink-0">
        <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-[var(--app-text)] mb-1">Generation</h3>
        <p className="text-xs text-[var(--app-text-muted)]">
          The models and spending limits that agents work within. Only you change them here; an agent
          can read them but not alter them.
        </p>
        <p className="text-xs text-[var(--app-text-muted)] mt-1">{LOCKED_NOTE}</p>
      </div>

      {loading ? (
        <p className="text-xs text-[var(--app-text-muted)]">Loading…</p>
      ) : (
        <div className="flex flex-col gap-4 max-w-xl" data-testid="generation-settings">
          {loadError && (
            <p className="text-xs text-amber-400" data-testid="generation-settings-load-error">
              Couldn't load your saved generation settings — showing the built-in defaults. Saving will
              overwrite whatever is stored.
            </p>
          )}

          <div>
            <Heading>Image model</Heading>
            <ImageProviderSelector
              provider={values.image.provider as ImageProvider}
              model={values.image.model}
              settings={(values.image.settings ?? {}) as Partial<MediaOutputSpec>}
              providerAriaLabel="Image provider"
              modelAriaLabel="Image model"
              settingsAriaLabel="Image model settings"
              {...imageHandlers}
            />
          </div>

          <div>
            <Heading>Carousel image size</Heading>
            <CarouselSizeField
              model={values.image.model}
              size={values.carousel_image}
              onChange={chooseCarouselSize}
              onMadeChange={setCarouselMade}
            />
          </div>

          <VideoModelsField video={values.video} onEdit={editVideo} />

          <AgentModelField
            value={values.agent_model}
            defaultChoice={values.agent_model_default}
            onChange={chooseAgentModel}
          />

          {values.audio && (
            <p className="text-[11px] text-[var(--app-text-muted)]">
              Audio model (reserved, nothing reads it yet): {values.audio.provider} · {values.audio.model}
            </p>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor={`${fieldId}-cap`}
                className="block text-[10px] uppercase tracking-widest text-[var(--app-text-muted)] mb-2"
              >
                Model cap per agent step (USD)
              </label>
              <input
                id={`${fieldId}-cap`}
                type="number"
                className="input w-full"
                min={GENERATION_MODEL_CAP_USD.min}
                max={GENERATION_MODEL_CAP_USD.max}
                step={GENERATION_MODEL_CAP_USD.step}
                value={capDraft}
                onChange={(e) => {
                  setSaved(false);
                  setError('');
                  setCapDraft(e.target.value);
                }}
              />
              <p className="mt-1 text-[11px] text-[var(--app-text-muted)]">
                Covers the agent's own model calls only, not the images and videos it orders.
              </p>
            </div>
            <div>
              <label
                htmlFor={`${fieldId}-generations`}
                className="block text-[10px] uppercase tracking-widest text-[var(--app-text-muted)] mb-2"
              >
                Max image generations per agent
              </label>
              <input
                id={`${fieldId}-generations`}
                type="number"
                className="input w-full"
                min={GENERATION_MAX_IMAGE_GENERATIONS.min}
                max={GENERATION_MAX_IMAGE_GENERATIONS.max}
                step={1}
                value={generationsDraft}
                onChange={(e) => {
                  setSaved(false);
                  setError('');
                  setGenerationsDraft(e.target.value);
                }}
              />
              <p className="mt-1 text-[11px] text-[var(--app-text-muted)]">
                The most image.generate calls one agent may make, retries included.
              </p>
            </div>
          </div>

          {error && (
            <p role="alert" className="text-xs text-red-400">
              {error}
            </p>
          )}

          <div className="flex items-center justify-end gap-3">
            {saved && (
              <span role="status" className="text-xs text-[var(--app-text-muted)]">
                Saved.
              </span>
            )}
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={saving || !complete}
              className="btn btn-primary btn-sm"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      )}

      {/* The same panel the chat's gear opens: one source, two doors. It reads and saves on its own. */}
      <div className="mt-6 border-t border-[var(--border-subtle)] pt-4" data-testid="agent-tools-section">
        <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-[var(--app-text)] mb-1">Agent tools</h3>
        <p className="mb-4 text-xs text-[var(--app-text-muted)]">
          What the chat agent&apos;s own tool calls may use and spend. Pipelines and the settings above are unchanged
          by them.
        </p>
        <AgentToolsPanel />
      </div>
    </>
  );
};

export default GenerationSettingsSection;
